import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bars } from "react-loader-spinner";
import Swal from "sweetalert2";
import { useQuery, useMutation, useQueryClient } from "react-query";
import axiosInstance from "../../Shared/axiosInstance"; 

export default function AllSuppliers() {
  const queryClient = useQueryClient();

  // Search & Pagination State
  const [searchName, setSearchName] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Fetch Suppliers
  const { data, isLoading, isFetching } = useQuery(
    ["suppliers", searchName, searchPhone, currentPage, pageSize],
    async () => {
      const params = { pageNumber: currentPage, pageSize };
      if (searchName.trim()) params.name = searchName.trim();
      if (searchPhone.trim()) params.phone = searchPhone.trim();

      const res = await axiosInstance.get("/Supplier/all", { params });
      return res.data;
    },
    { keepPreviousData: true }
  );

  // Delete Mutation
  const deleteMutation = useMutation(
    async (id) => {
      await axiosInstance.delete(`/Supplier/${id}`);
    },
    {
      onSuccess: async () => {
        await Swal.fire({
          title: "تم الحذف!",
          text: "تم حذف المورد بنجاح.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        queryClient.invalidateQueries(["suppliers"]);
      },
      onError: (error) => {
        const msg = error?.response?.data?.message || "فشل في حذف المورد.";
        Swal.fire("فشل الحذف", msg, "error");
      },
    }
  );

  const handleDelete = (id) => {
    Swal.fire({
      title: "هل أنت متأكد؟",
      text: "لا يمكنك التراجع بعد الحذف!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "نعم، احذف!",
      cancelButtonText: "إلغاء",
    }).then((result) => {
      if (result.isConfirmed) deleteMutation.mutate(id);
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > (data?.totalPages || 1)) return;
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => setCurrentPage(1), 500);
    return () => clearTimeout(timer);
  }, [searchName, searchPhone]);

  if (isLoading) {
    return (
      <div className="vh-100 d-flex align-items-center justify-content-center flex-column">
        <Bars height="80" width="80" color="#2563eb" ariaLabel="loading" />
        <p className="mt-3" style={{ color: "#64748b", fontSize: "18px" }}>
          جاري تحميل الموردين...
        </p>
      </div>
    );
  }

  const suppliers = data?.data || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = data?.totalPages || 0;

  return (
    <div className="container py-5" style={{ maxWidth: "1200px" }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 mt-5 pt-5">
        <div>
          <h2 style={{ color: "#2563eb", fontWeight: "700", fontSize: "28px", marginBottom: "4px" }}>
            قائمة الموردين
          </h2>
          <small style={{ color: "#64748b", fontSize: "14px" }}>
            عرض {suppliers.length} من أصل {totalCount} مورد
          </small>
        </div>
        <Link to="/add-supplier">
          <button style={addBtnStyle}>
            <i className="bi bi-plus-circle me-1"></i> إضافة مورد
          </button>
        </Link>
      </div>

      {/* Search */}
      <div style={searchBoxStyle}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 style={searchTitleStyle}>بحث عن موردين</h5>
          {isFetching && (
            <div className="d-flex align-items-center gap-2">
              <div className="spinner-border spinner-border-sm" role="status" style={{ color: "#2563eb", width: "18px", height: "18px" }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <small style={{ color: "#64748b", fontSize: "13px" }}>جارٍ البحث...</small>
            </div>
          )}
        </div>
        <div className="row g-3">
          <div className="col-md-4">
            <label style={labelStyle}>البحث بالاسم</label>
            <input type="text" placeholder="ادخل اسم المورد" value={searchName} onChange={(e) => setSearchName(e.target.value)} style={inputStyle} />
          </div>
          <div className="col-md-4">
            <label style={labelStyle}>البحث بالهاتف</label>
            <input type="text" placeholder="ادخل رقم الهاتف" value={searchPhone} onChange={(e) => setSearchPhone(e.target.value)} style={inputStyle} />
          </div>
          <div className="col-md-4">
            <label style={labelStyle}>عدد العناصر في الصفحة</label>
            <select value={pageSize} onChange={(e) => handlePageSizeChange(Number(e.target.value))} style={inputStyle}>
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Info */}
      {suppliers.length > 0 && (
        <div className="mb-3">
          <span style={{ color: "#64748b", fontSize: "14px" }}>
            عرض {((currentPage - 1) * pageSize) + 1} إلى {Math.min(currentPage * pageSize, totalCount)} من أصل {totalCount} مورد
          </span>
        </div>
      )}

      {/* Table */}
      <div className="table-responsive">
        <table className="table mb-0" style={tableStyle(isFetching)}>
          <thead style={{ background: "#dbeafe" }}>
            <tr>
              <th style={thStyle}>#</th>
              <th style={thStyle}>الاسم</th>
              <th style={thStyle}>الهاتف</th>
              <th style={thStyle}>التفاصيل</th>
              <th style={thStyle}>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.length > 0 ? (
              suppliers.map((sup, idx) => (
                <tr key={sup.id} style={{ background: idx % 2 === 0 ? "#f8fafc" : "#fff" }}>
                  <td style={tdStyle}>{((currentPage - 1) * pageSize) + idx + 1}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{sup.name}</td>
                  <td style={tdStyle}>{sup.phone}</td>
                  <td style={tdStyle}>{sup.details || "-"}</td>
                  <td style={tdStyle}>
                    <div className="d-flex gap-2 justify-content-center flex-wrap">
                      <Link to={`/suppliers/${sup.id}/purchases`}>
                        <button style={actionBtnStyle("#22c55e")}>
                          <i className="bi bi-eye me-1"></i> عرض المشتريات
                        </button>
                      </Link>
                      <Link to={`/update-supplier/${sup.id}`}>
                        <button style={actionBtnStyle("#2563eb")}>
                          <i className="bi bi-pencil-square me-1"></i> تعديل
                        </button>
                      </Link>
                      <button style={actionBtnStyle("#dc2626")} onClick={() => handleDelete(sup.id)}>
                        <i className="bi bi-trash me-1"></i> حذف
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ ...tdStyle, color: "#64748b", padding: "25px" }}>
                  {isFetching ? "جارٍ البحث..." : "لا يوجد موردين"}
                  {!isFetching && (
                    <div className="mt-3">
                      <Link to="/add-supplier">
                        <button style={addBtnStyle}>إضافة المورد الأول</button>
                      </Link>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div style={{ color: "#64748b", fontSize: "14px" }}>
            الصفحة {currentPage} من {totalPages}
          </div>
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} style={paginationBtnStyle}>
                  السابق
                </button>
              </li>
              {[...Array(totalPages)].map((_, i) => (
                <li key={i}>
                  <button onClick={() => handlePageChange(i + 1)} style={{
                    ...paginationBtnStyle,
                    background: currentPage === i + 1 ? "#2563eb" : "#fff",
                    color: currentPage === i + 1 ? "#fff" : "#475569",
                    fontWeight: currentPage === i + 1 ? "600" : "500",
                  }}>{i + 1}</button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} style={paginationBtnStyle}>
                  التالي
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
}

// ✅ Styles
const thStyle = { color: "#1e40af", fontSize: "15px", padding: "12px", fontWeight: "700", border: "1px solid #bfdbfe" };
const tdStyle = { padding: "12px", border: "1px solid #e0e7ff", fontSize: "19px", color: "#475569", fontWeight: "600" };
const actionBtnStyle = (color) => ({ background: color, color: "#fff", padding: "6px 12px", borderRadius: "6px", border: "none", fontWeight: "600", fontSize: "16px", cursor: "pointer", transition: "all 0.3s ease" });
const paginationBtnStyle = { padding: "8px 14px", margin: "0 3px", border: "1px solid #bfdbfe", borderRadius: "6px", fontSize: "14px", cursor: "pointer", transition: "all 0.2s ease" };
const addBtnStyle = { background: "#2563eb", color: "#fff", padding: "10px 20px", borderRadius: "6px", border: "none", fontWeight: "600", fontSize: "15px", boxShadow: "0 2px 8px rgba(37, 99, 235, 0.3)", transition: "all 0.3s ease" };
const searchBoxStyle = { background: "#fff", border: "1px solid #dbeafe", borderRadius: "8px", padding: "20px", boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)", marginBottom: "20px" };
const searchTitleStyle = { color: "#1e40af", fontWeight: "600", fontSize: "16px", margin: 0 };
const inputStyle = { width: "100%", padding: "10px 12px", border: "1px solid #bfdbfe", borderRadius: "6px", fontSize: "15px", color: "#475569", outline: "none", transition: "all 0.2s" };
const labelStyle = { color: "#475569", fontSize: "14px", fontWeight: "600", marginBottom: "6px" };
const tableStyle = (fetching) => ({ border: "1px solid #dbeafe", borderRadius: "8px", overflow: "hidden", boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)", textAlign: "center", opacity: fetching ? 0.6 : 1, transition: "opacity 0.2s" });
